<template>
    <ValidationObserver v-slot="{ handleSubmit }">
        <form @submit.prevent="handleSubmit(onSubmit)">
            <div class="p-5">
                <div
                    class="d-flex justify-content-between align-items-center mb-5"
                >
                    <div class="c-title">New Campaign</div>
                    <RouterLink class="ml-auto" to="/campaigns">
                        <button class="btn c-cancel-btn">Cancel</button>
                    </RouterLink>
                    <button type="submit" class="btn ml-3 c-save-btn">
                        Save
                    </button>
                </div>

                <div class="row">
                    <div class="col-7">
                        <SelectField
                            v-model="form.type"
                            name="type"
                            label="Type"
                            :options="types"
                            :has-icon="true"
                            show-tooltip
                            :tooltip-title="translation.trans('tooltips.campaigns.form.type.title')"
                            :tooltip-desc="translation.trans('tooltips.campaigns.form.type.description')"
                        >
                            <template #append-icon>
                                <a @click.prevent="() => {}">
                                    <i
                                        class="fa fa-chevron-circle-down mt-2"
                                        style="font-size: 20px; color: #40b8dc"
                                    />
                                </a>
                            </template>
                        </SelectField>
                        <TextField
                            v-model="form.title"
                            name="title"
                            label="Title"
                            rules="required"
                            show-tooltip
                            :tooltip-title="translation.trans('tooltips.campaigns.form.title.title')"
                            :tooltip-desc="translation.trans('tooltips.campaigns.form.title.description')"
                        />
                        <DatePickerField
                            v-model="form.start_date"
                            name="start_date"
                            label="Start Date"
                            rules="required"
                            show-tooltip
                            :tooltip-title="translation.trans('tooltips.campaigns.form.start_date.title')"
                            :tooltip-desc="translation.trans('tooltips.campaigns.form.start_date.description')"
                        />
                        <DatePickerField
                            v-model="form.end_date"
                            name="end_date"
                            label="End Date"
                            :disabled="disabledEndDate"
                            show-tooltip
                            :tooltip-title="translation.trans('tooltips.campaigns.form.end_date.title')"
                            :tooltip-desc="translation.trans('tooltips.campaigns.form.end_date.description')"
                        />
                        <TextareaField
                            v-model="form.about_short"
                            name="about_short"
                            label="About the Campaign - Short Description"
                            rules="required"
                            rows="5"
                            classWrapper="mb-4"
                            show-tooltip
                            :tooltip-title="translation.trans('tooltips.campaigns.form.short_description.title')"
                            :tooltip-desc="translation.trans('tooltips.campaigns.form.short_description.description')"
                        />
                        <TextareaField
                            v-model="form.about_campaign"
                            name="about_campaign"
                            label="Full Description"
                            rows="5"
                            classWrapper="mb-4"
                            show-tooltip
                            :tooltip-title="translation.trans('tooltips.campaigns.form.description.title')"
                            :tooltip-desc="translation.trans('tooltips.campaigns.form.description.description')"
                        />
                        <TextareaField
                            v-model="form.thankyou"
                            name="thankyou"
                            label="Thank You Note"
                            rules="required"
                            rows="5"
                            classWrapper="mb-4"
                            show-tooltip
                            :tooltip-title="translation.trans('tooltips.campaigns.form.thank.title')"
                            :tooltip-desc="translation.trans('tooltips.campaigns.form.thank.description')"
                        />
                    </div>

                    <div class="col-5">
                        <TextField
                            v-model="form.goal_amount"
                            name="goal_amount"
                            label="Goal Amount"
                            rules="required|numeric"
                            show-tooltip
                            :tooltip-title="translation.trans('tooltips.campaigns.form.goal_amount.title')"
                            :tooltip-desc="translation.trans('tooltips.campaigns.form.goal_amount.description')"
                        />
                        <TextField
                            v-model="form.slam_amount"
                            name="slam_amount"
                            label="Slam Amount"
                            rules="required|numeric"
                            show-tooltip
                            :tooltip-title="translation.trans('tooltips.campaigns.form.slam_amount.title')"
                            :tooltip-desc="translation.trans('tooltips.campaigns.form.slam_amount.description')"
                        />

                        <FormLabel
                            show-tooltip
                            :tooltip-title="translation.trans('tooltips.campaigns.form.amount_options.title')"
                            :tooltip-desc="translation.trans('tooltips.campaigns.form.amount_options.description')"
                            label="Amount Options"
                        />
                        <div class="row">
                            <div
                                class="col-6"
                                v-for="(option, index) in form.amount_options"
                                :key="index"
                            >
                                <TextField
                                    v-model="form.amount_options[index]"
                                    :name="`amount_options[${index}]`"
                                    :label="`Option ${index + 1}`"
                                />
                            </div>
                        </div>

                        <FormLabel
                            show-tooltip
                            :tooltip-title="translation.trans('tooltips.campaigns.form.images.title')"
                            :tooltip-desc="translation.trans('tooltips.campaigns.form.images.description')"
                            label="Images"
                        />
                        <div class="c-upload">
                            <UploadImage
                                v-for="(image, index) in form.images"
                                :key="index"
                                v-model="form.images[index]"
                                :name="`images[${index}]`"
                                :removable="true"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </ValidationObserver>
</template>

<script>
import TextField from '@/commons/FormElements/TextField.vue';
import FormLabel from '@/commons/Typography/FormLabel.vue';
import TextareaField from '@/commons/FormElements/TextareaField.vue';
import DatePickerField from '@/commons/FormElements/DatePickerField.vue';
import SelectField from '@/commons/FormElements/SelectField.vue';
import UploadImage from '@/commons/FormElements/UploadImage.vue';
import { TYPE_IDS } from './constants/index';
import DibbleJs from '@/dibble';
import DibbleApi from '@/api';
import DibbleTranslation from "@/translation/translation";

export default {
    name: 'CreateView',
    components: {
        TextField,
        TextareaField,
        SelectField,
        UploadImage,
        DatePickerField,
        FormLabel,
    },
    data() {
        return {
            translation: DibbleTranslation,
            form: {
                type: '',
                title: '',
                start_date: '',
                end_date: '',
                about_short: '',
                about_campaign: '',
                thankyou: '',
                goal_amount: '',
                amount_options: ['', '', '', '', '', ''],
                slam_amount: '',
                images: ['', '', ''],
            },
            types: [
                { value: TYPE_IDS.ON_GOING, label: 'On Going' },
                { value: TYPE_IDS.TIME_SPANNED, label: 'Time Spanned' },
                { value: TYPE_IDS.EVENT, label: 'Event' },
            ],
        };
    },
    computed: {
        disabledEndDate() {
            return this.form.type === TYPE_IDS.ON_GOING;
        },
    },
    methods: {
        onSubmit() {
            const amount_options = [];
            this.form.amount_options.forEach((option) => {
                if (option) {
                    amount_options.push(option);
                }
            });
            if (amount_options.length === 0) {
                DibbleJs.toastErrorMessage(
                    'At least one amount option is required.'
                );
                return;
            }

            const images = [];
            this.form.images.forEach((image) => {
                if (image) {
                    images.push(image);
                }
            });
            if (images.length === 0) {
                DibbleJs.toastErrorMessage('At least one image is required.');
                return;
            }

            DibbleJs.showLoader();
            const data = {
                '#request': DibbleApi.api.addCampaign,
                ...this.form,
                amount_options: amount_options.join(', '),
                goal_amount: Number(this.form.goal_amount),
                slam_amount: Number(this.form.slam_amount),
                type: Number(this.form.type),
                images: images.map((image) => ({
                    image,
                })),
            };
            DibbleJs.callApiRequest(data, (response) => {
                if (DibbleApi.isResponseSuccess(response)) {
                    DibbleJs.toastSuccessMessage(
                        'Create campaign successfully'
                    );
                    this.$router.push({
                        name: 'CampaignsView',
                        params: {
                            id: response.campaign_id,
                        },
                    });
                } else {
                    DibbleJs.toastErrorMessage(response.message);
                }
                DibbleJs.hideLoader();
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.c-title {
    color: #2178ab;
    font-size: 22px;
    font-weight: 700;
}
.c-cancel-btn {
    border-radius: 20px;
    background: #d9d9d9;
    box-shadow: 0px 13px 30px -12px rgba(0, 0, 0, 0.25);
    font-size: 20px;
    font-weight: 700;
    width: 120px;
    color: #555e67;
    line-height: 140%;
}
.c-save-btn {
    border-radius: 20px;
    background: #2178ab;
    box-shadow: 0px 13px 30px -12px rgba(0, 0, 0, 0.25);
    font-size: 20px;
    font-weight: 700;
    color: #fff;
    width: 120px;
    line-height: 140%;
}
.c-upload {
    background-color: #e1e7e9;
    border-radius: 20px;
    padding: 24px 32px;
    box-shadow: 0px 13px 30px -12px rgba(0, 0, 0, 0.25);
}
</style>
